// src/pages/ContractInsightDashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getContractInsightStatus,
  getLiveView,
  getSourceFile,
  getReport,
  createContractInsightBrowserTicket,
} from "../utils/contractApi";
import ContractFileViewer from "../components/ContractFileViewer";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function useIsContractDashboardCompact(breakpoint = 1024) {
  const getInitial = () =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false;

  const [isCompact, setIsCompact] = useState(getInitial());

  useEffect(() => {
    const onResize = () => setIsCompact(getInitial());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isCompact;
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
}

function pickFirstNumeric(...values) {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
  }
  return "";
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function cleanDisplayText(value, fallback = "—") {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : fallback;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const joined = value.map((item) => cleanDisplayText(item, "")).filter(Boolean).join(", ");
    return joined || fallback;
  }
  if (isPlainObject(value)) {
    const preferred =
      value.title ||
      value.label ||
      value.name ||
      value.summary ||
      value.text ||
      value.heading ||
      value.issue;
    return preferred ? cleanDisplayText(preferred, fallback) : fallback;
  }
  const text = String(value).replace(/\s+/g, " ").trim();
  return text || fallback;
}

function formatDateTime(value) {
  if (!value) return "";
  try {
    const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return "";
  }
}

function getByPath(obj, path, fallback = "") {
  const parts = String(path || "").split(".").filter(Boolean);
  let cur = obj;
  for (const part of parts) {
    if (cur === undefined || cur === null) return fallback;
    cur = cur?.[part];
  }
  return cur === undefined || cur === null || cur === "" ? fallback : cur;
}

function clamp(n, min = 0, max = 100) {
  return Math.max(min, Math.min(max, Number(n) || 0));
}

function normalizeStatus(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "processing";
  if (raw === "complete" || raw === "completed" || raw === "report_ready") return "complete";
  if (raw === "error" || raw === "failed" || raw === "failure" || raw === "billing_error") return "error";
  return raw;
}

function isComplete(value) {
  return normalizeStatus(value) === "complete";
}

function normalizeRiskLevel(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "high") return "High";
  if (raw === "medium") return "Medium";
  if (raw === "low") return "Low";
  return cleanDisplayText(value, "Pending");
}

function buildTone({ label, color, bg, border, softBg, glow }) {
  return {
    label,
    color,
    bg,
    border,
    softBg: softBg || bg,
    glow: glow || `${color}1A`,
  };
}

function riskColor(level) {
  const raw = String(level || "").trim().toLowerCase();
  if (raw === "high") {
    return buildTone({
      label: "High",
      color: "#B91C1C",
      bg: "#FEF2F2",
      border: "#FECACA",
      softBg: "#FFF7F7",
      glow: "rgba(185,28,28,0.16)",
    });
  }
  if (raw === "medium") {
    return buildTone({
      label: "Medium",
      color: "#C2410C",
      bg: "#FFF7ED",
      border: "#FED7AA",
      softBg: "#FFFBF6",
      glow: "rgba(194,65,12,0.16)",
    });
  }
  if (raw === "low") {
    return buildTone({
      label: "Low",
      color: "#15803D",
      bg: "#ECFDF5",
      border: "#BBF7D0",
      softBg: "#F7FEFB",
      glow: "rgba(21,128,61,0.16)",
    });
  }
  return buildTone({
    label: cleanDisplayText(level, "Pending"),
    color: "#475569",
    bg: "#F8FAFC",
    border: "#E2E8F0",
    softBg: "#FFFFFF",
    glow: "rgba(71,85,105,0.13)",
  });
}

function riskToneFromScore(score, fallbackLevel = "") {
  const n = Number(score);
  if (!Number.isFinite(n)) return riskColor(fallbackLevel);

  // Contract Insight risk score is risk-oriented:
  // low risk below 40, medium risk from 40-69.9, high risk at 70+.
  if (n >= 70) return riskColor("high");
  if (n >= 40) return riskColor("medium");
  return riskColor("low");
}

function liveViewColor() {
  return buildTone({
    label: "Live View",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    softBg: "#F8FBFF",
    glow: "rgba(37,99,235,0.16)",
  });
}

function formatScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function contractTypeLabel(value) {
  const text = cleanDisplayText(value, "Contract");
  return text
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .replace(/Api\b/g, "API");
}

function asPages(liveView) {
  if (Array.isArray(liveView?.pages)) return liveView.pages;
  if (Array.isArray(liveView?.liveView?.pages)) return liveView.liveView.pages;
  if (Array.isArray(liveView?.live_view?.pages)) return liveView.live_view.pages;
  return [];
}

function paragraphRiskStyle(level) {
  const raw = String(level || "").trim().toLowerCase();
  if (raw === "high") return styles.liveTileHigh;
  if (raw === "medium") return styles.liveTileMedium;
  if (raw === "low") return styles.liveTileLow;
  return {};
}

function paragraphRiskPillStyle(level) {
  const raw = String(level || "").trim().toLowerCase();
  if (raw === "high") return styles.riskPillHigh;
  if (raw === "medium") return styles.riskPillMedium;
  if (raw === "low") return styles.riskPillLow;
  return styles.riskPillNeutral;
}

function countParagraphsFromPages(pages = []) {
  if (!Array.isArray(pages)) return 0;
  return pages.reduce((total, page) => {
    const paragraphs = Array.isArray(page?.paragraphs) ? page.paragraphs : [];
    return total + paragraphs.length;
  }, 0);
}

function buildLiveViewSummary({ pageCount, paragraphCount, ready }) {
  if (!ready) {
    return "This Contract Insight job is still processing. Open the status page to watch progress.";
  }

  const pagesText = pageCount ? `${pageCount} page${Number(pageCount) === 1 ? "" : "s"}` : "the analyzed pages";
  const highlightsText = paragraphCount
    ? `${paragraphCount} Live View highlight${Number(paragraphCount) === 1 ? "" : "s"}`
    : "Live View highlights";

  return `This contract includes ${highlightsText} across ${pagesText}. Review the original contract beside the Live View to understand key clause highlights and explanations.`;
}

function normalizeCoverageCount(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.trunc(value));
  }
  if (typeof value === "string" && value.trim() !== "") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return Math.max(0, Math.trunc(numeric));
  }
  return null;
}

function getCoverageSnapshotFromPayload(...sources) {
  const candidates = [];

  for (const source of sources) {
    if (!isPlainObject(source)) continue;

    candidates.push(
      source?.coverageSummary,
      source?.coverage_summary,
      getByPath(source, "report.coverageSummary", null),
      getByPath(source, "report.coverage_summary", null),
      getByPath(source, "report.summaryReport.executiveSnapshot.coverage", null),
      getByPath(source, "report.summary_report.executive_snapshot.coverage", null),
      getByPath(source, "summaryReport.executiveSnapshot.coverage", null),
      getByPath(source, "summary_report.executive_snapshot.coverage", null)
    );
  }

  for (const candidate of candidates) {
    if (!isPlainObject(candidate)) continue;

    const found = normalizeCoverageCount(
      pickFirst(candidate?.found, candidate?.present)
    );
    const unclear = normalizeCoverageCount(
      pickFirst(candidate?.unclear, candidate?.partial)
    );
    const notConfirmed = normalizeCoverageCount(
      pickFirst(candidate?.notConfirmed, candidate?.not_confirmed, candidate?.unknown)
    );
    const notFound = normalizeCoverageCount(
      pickFirst(candidate?.notFound, candidate?.not_found, candidate?.absent)
    );

    if ([found, unclear, notConfirmed, notFound].some((value) => value !== null)) {
      return { found, unclear, notConfirmed, notFound };
    }
  }

  return {
    found: null,
    unclear: null,
    notConfirmed: null,
    notFound: null,
  };
}

function getAnalyzedPdfUrlFromPayload(data) {
  return pickFirst(
    data?.analyzedPdfUrl,
    data?.analyzed_pdf_url,
    data?.ingestPdfUrl,
    data?.ingest_pdf_url,
    data?.normalizedPdfUrl,
    data?.normalized_pdf_url,
    data?.canonicalPdfUrl,
    data?.canonical_pdf_url,
    getByPath(data, "ingest.analyzedPdfUrl"),
    getByPath(data, "ingest.analyzed_pdf_url"),
    getByPath(data, "ingest.ingestPdfUrl"),
    getByPath(data, "ingest.ingest_pdf_url"),
    getByPath(data, "ingest.normalizedPdfUrl"),
    getByPath(data, "ingest.normalized_pdf_url"),
    getByPath(data, "ingest.canonicalPdfUrl"),
    getByPath(data, "ingest.canonical_pdf_url"),
    getByPath(data, "artifacts.analyzedPdfUrl"),
    getByPath(data, "artifacts.analyzed_pdf_url"),
    getByPath(data, "artifacts.ingestPdfUrl"),
    getByPath(data, "artifacts.ingest_pdf_url"),
    getByPath(data, "artifacts.normalizedPdfUrl"),
    getByPath(data, "artifacts.normalized_pdf_url"),
    getByPath(data, "job.analyzedPdfUrl"),
    getByPath(data, "job.analyzed_pdf_url"),
    getByPath(data, "job.ingestPdfUrl"),
    getByPath(data, "job.ingest_pdf_url"),
    getByPath(data, "job.normalizedPdfUrl"),
    getByPath(data, "job.normalized_pdf_url"),
    getByPath(data, "job.ingest.analyzedPdfUrl"),
    getByPath(data, "job.ingest.analyzed_pdf_url"),
    getByPath(data, "job.ingest.ingestPdfUrl"),
    getByPath(data, "job.ingest.ingest_pdf_url"),
    getByPath(data, "job.ingest.normalizedPdfUrl"),
    getByPath(data, "job.ingest.normalized_pdf_url"),
    getByPath(data, "job.artifacts.analyzedPdfUrl"),
    getByPath(data, "job.artifacts.analyzed_pdf_url"),
    getByPath(data, "job.artifacts.ingestPdfUrl"),
    getByPath(data, "job.artifacts.ingest_pdf_url"),
    getByPath(data, "job.artifacts.normalizedPdfUrl"),
    getByPath(data, "job.artifacts.normalized_pdf_url")
  );
}

function normalizeSummaryCandidate(value) {
  if (value === undefined || value === null || value === "") return "";

  if (typeof value === "string") {
    return cleanDisplayText(value, "");
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return cleanDisplayText(value, "");
  }

  if (Array.isArray(value)) {
    const parts = value
      .map((item) => {
        if (typeof item === "string") return cleanDisplayText(item, "");
        if (isPlainObject(item)) {
          return normalizeSummaryCandidate(
            pickFirst(
              item?.dashboardSummary,
              item?.dashboard_summary,
              item?.executiveSummary,
              item?.executive_summary,
              item?.overallFinding,
              item?.overall_finding,
              item?.summary,
              item?.text,
              item?.body,
              item?.content,
              item?.finding,
              item?.description
            )
          );
        }
        return "";
      })
      .filter(Boolean);

    return cleanDisplayText(parts.join(" "), "");
  }

  if (isPlainObject(value)) {
    const preferred = pickFirst(
      value?.dashboardSummary,
      value?.dashboard_summary,
      value?.executiveSummary,
      value?.executive_summary,
      value?.overallSummary,
      value?.overall_summary,
      value?.overallFinding,
      value?.overall_finding,
      value?.summaryText,
      value?.summary_text,
      value?.text,
      value?.body,
      value?.content,
      value?.overview,
      value?.conclusion,
      value?.finding,
      value?.description,
      value?.summary
    );

    if (preferred && preferred !== value) {
      return normalizeSummaryCandidate(preferred);
    }
  }

  return "";
}

function extractSummaryFromSections(sections) {
  if (!sections) return "";

  const sectionMatches = (section) => {
    const label = cleanDisplayText(
      pickFirst(section?.title, section?.heading, section?.label, section?.name, section?.sectionTitle),
      ""
    ).toLowerCase();

    return (
      label.includes("dashboard summary") ||
      label.includes("executive summary") ||
      label === "summary" ||
      label.includes("overall finding") ||
      label.includes("overall summary")
    );
  };

  if (Array.isArray(sections)) {
    for (const section of sections) {
      if (!isPlainObject(section) || !sectionMatches(section)) continue;
      const text = normalizeSummaryCandidate(
        pickFirst(
          section?.dashboardSummary,
          section?.dashboard_summary,
          section?.executiveSummary,
          section?.executive_summary,
          section?.overallFinding,
          section?.overall_finding,
          section?.summary,
          section?.text,
          section?.body,
          section?.content,
          section?.paragraphs,
          section?.items
        )
      );
      if (text) return text;
    }
  }

  if (isPlainObject(sections)) {
    for (const [key, section] of Object.entries(sections)) {
      const keyText = String(key || "").replace(/[_-]+/g, " ").toLowerCase();
      const keyLooksRight =
        keyText.includes("dashboard summary") ||
        keyText.includes("executive summary") ||
        keyText.includes("overall finding") ||
        keyText.includes("overall summary") ||
        keyText === "summary";

      if (!keyLooksRight && !(isPlainObject(section) && sectionMatches(section))) {
        continue;
      }

      const text = normalizeSummaryCandidate(section);
      if (text) return text;
    }
  }

  return "";
}

function extractSummaryTextFromObject(obj) {
  if (!isPlainObject(obj)) return "";

  const directCandidates = [
    obj?.dashboardSummary,
    obj?.dashboard_summary,
    obj?.executiveSummary,
    obj?.executive_summary,
    obj?.overallSummary,
    obj?.overall_summary,
    obj?.overallFinding,
    obj?.overall_finding,
    obj?.summaryText,
    obj?.summary_text,
    obj?.summary,
    obj?.overview,
    obj?.conclusion,
  ];

  for (const candidate of directCandidates) {
    const text = normalizeSummaryCandidate(candidate);
    if (text) return text;
  }

  const pathCandidates = [
    "meta.dashboardSummary",
    "meta.dashboard_summary",
    "meta.executiveSummary",
    "meta.executive_summary",
    "meta.overallSummary",
    "meta.overall_summary",
    "meta.overallFinding",
    "meta.overall_finding",
    "meta.summaryText",
    "meta.summary_text",
    "meta.summary",
    "metadata.dashboardSummary",
    "metadata.dashboard_summary",
    "metadata.executiveSummary",
    "metadata.executive_summary",
    "metadata.overallFinding",
    "metadata.overall_finding",
    "metadata.summary",
    "dashboard.summary",
    "dashboard.dashboardSummary",
    "dashboard.dashboard_summary",
    "dashboard.executiveSummary",
    "dashboard.executive_summary",
    "summary.overallFinding",
    "summary.overall_finding",
    "summary.executiveSummary",
    "summary.executive_summary",
    "summary.dashboardSummary",
    "summary.dashboard_summary",
    "summary.text",
    "summary.summary",
    "executiveSummary.text",
    "executive_summary.text",
    "report.summary.overallFinding",
    "report.summary.overall_finding",
    "report.summary.executiveSummary",
    "report.summary.executive_summary",
    "report.summary.dashboardSummary",
    "report.summary.dashboard_summary",
    "report.executiveSummary",
    "report.executive_summary",
    "report.dashboardSummary",
    "report.dashboard_summary",
    "finalReport.summary",
    "final_report.summary",
    "finalReport.executiveSummary",
    "final_report.executive_summary",
    "finalReport.dashboardSummary",
    "final_report.dashboard_summary",
    "liveView.dashboardSummary",
    "liveView.dashboard_summary",
    "liveView.executiveSummary",
    "liveView.executive_summary",
    "live_view.dashboardSummary",
    "live_view.dashboard_summary",
    "live_view.executiveSummary",
    "live_view.executive_summary",
  ];

  for (const path of pathCandidates) {
    const text = normalizeSummaryCandidate(getByPath(obj, path));
    if (text) return text;
  }

  return (
    extractSummaryFromSections(obj?.sections) ||
    extractSummaryFromSections(obj?.reportSections) ||
    extractSummaryFromSections(obj?.report_sections) ||
    extractSummaryFromSections(obj?.finalReport?.sections) ||
    extractSummaryFromSections(obj?.final_report?.sections) ||
    ""
  );
}

export default function ContractInsightDashboard() {
  const q = useQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const isCompact = useIsContractDashboardCompact();

  const jobId = q.get("jobId") || location.state?.jobId || "";
  const routeStateToken = typeof location.state?.idToken === "string" ? location.state.idToken : "";
  const idTokenRef = useRef(routeStateToken || "");

  const [idToken, setIdToken] = useState(routeStateToken || "");
  const [authReady, setAuthReady] = useState(Boolean(routeStateToken));
  const [payload, setPayload] = useState(null);
  const [liveView, setLiveView] = useState(null);
  const [liveViewPayload, setLiveViewPayload] = useState(null);
  const [source, setSource] = useState(null);
  const [reportRoutePayload, setReportRoutePayload] = useState(null);
  const [error, setError] = useState("");
  const [liveViewError, setLiveViewError] = useState("");
  const [sourceError, setSourceError] = useState("");
  const [artifactLoading, setArtifactLoading] = useState("");
  const [artifactError, setArtifactError] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedTileKey, setExpandedTileKey] = useState(null);
  const [expandedPageKey, setExpandedPageKey] = useState(null);
  const [activeMobileTab, setActiveMobileTab] = useState("contract");
  const [desktopViewMode, setDesktopViewMode] = useState("both");
  const [documentView, setDocumentView] = useState("analyzed");

  const selectDocumentView = (nextView) => {
    setDocumentView(nextView === "original" ? "original" : "analyzed");
  };

  useEffect(() => {
    setDocumentView("analyzed");
  }, [jobId]);

  useEffect(() => {
    if (!jobId) {
      navigate("/contract-insight/history", { replace: true });
    }
  }, [jobId, navigate]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        idTokenRef.current = "";
        setIdToken("");
        setAuthReady(true);
        setLoading(false);
        return;
      }

      try {
        const token = await user.getIdToken();
        idTokenRef.current = token;
        setIdToken(token);
      } catch (err) {
        setError(err?.message || "Unable to verify your login session.");
      } finally {
        setAuthReady(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const getFreshToken = async ({ forceRefresh = false } = {}) => {
    const user = getAuth()?.currentUser;
    if (!user) throw new Error("Please sign in again to view this dashboard.");
    const token = await user.getIdToken(forceRefresh);
    idTokenRef.current = token;
    setIdToken(token);
    return token;
  };

  useEffect(() => {
    if (!authReady || !jobId) return;
    if (!idTokenRef.current && !idToken) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function loadDashboard() {
      setLoading(true);
      setError("");
      setLiveViewError("");
      setSourceError("");
      setArtifactError("");
      setLiveViewPayload(null);

      try {
        const token = idTokenRef.current || idToken || (await getFreshToken());
        const statusPayload = await getContractInsightStatus(jobId, { idToken: token });
        if (cancelled) return;
        setPayload(statusPayload || {});

        const rawJob = statusPayload?.job || statusPayload || {};
        const rawStatus = rawJob?.status || statusPayload?.status || rawJob?.progress?.stage;
        if (!isComplete(rawStatus)) {
          setLoading(false);
          return;
        }

        const [liveResult, sourceResult, reportResult] = await Promise.allSettled([
          getLiveView(jobId, { idToken: token }),
          getSourceFile(jobId, { idToken: token }),
          getReport(jobId, { idToken: token }),
        ]);

        if (cancelled) return;

        if (liveResult.status === "fulfilled") {
          const res = liveResult.value || {};
          const raw = res?.liveView || res?.thread || res || {};
          setLiveViewPayload(res || {});
          setLiveView(raw?.liveView || raw?.live_view || raw);
        } else {
          setLiveViewPayload(null);
          setLiveViewError(liveResult.reason?.message || "Unable to load Live View.");
        }

        if (sourceResult.status === "fulfilled") {
          const res = sourceResult.value || {};
          if (res?.ok && res?.url) {
            setSource(res);
          } else if (res?.url) {
            setSource(res);
          } else {
            setSourceError("Original contract preview is not available.");
          }
        } else {
          setSourceError(sourceResult.reason?.message || "Unable to load original contract.");
        }

        if (reportResult.status === "fulfilled") {
          const reportRes = reportResult.value || {};
          setReportRoutePayload(reportRes);
        } else {
          setReportRoutePayload(null);
        }

        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Unable to load Contract Insight dashboard.");
          setPayload(null);
          setLiveViewPayload(null);
          setReportRoutePayload(null);
          setLoading(false);
        }
      }
    }

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [authReady, idToken, jobId]);

  const openReportArtifact = async (artifactKind) => {
    if (!jobId || artifactLoading) return;

    const config = {
      summary: {
        loadingKey: "summary-pdf",
        label: "Summary Report",
        artifactKey: "summary",
      },
      pageReview: {
        loadingKey: "page-review-pdf",
        label: "Page Review",
        artifactKey: "page-review",
      },
      termsGuide: {
        loadingKey: "terms-guide-pdf",
        label: "Terms Guide",
        artifactKey: "terms-guide",
      },
    }[artifactKind];

    if (!config) return;

    setArtifactLoading(config.loadingKey);
    setArtifactError("");

    // Open the tab synchronously from the user's click so browsers do not
    // block it after the async Firebase/token + browser-ticket request.
    const browserTab = window.open("about:blank", "_blank");
    if (browserTab) {
      try {
        browserTab.opener = null;
        browserTab.document.title = `Opening ${config.label}…`;
      } catch (_) {
        // Navigation still works even if the temporary blank tab is restricted.
      }
    }

    try {
      const token = idTokenRef.current || idToken || (await getFreshToken());

      // Always request a brand-new opaque AskLawly browser ticket.
      // Never open a stored/signed GCS artifact URL directly.
      const ticket = await createContractInsightBrowserTicket(
        jobId,
        config.artifactKey,
        { idToken: token }
      );

      const browserUrl = String(ticket?.browserUrl || "").trim();
      if (!browserUrl) {
        throw new Error(
          `${config.label} browser access is not available for this analysis yet.`
        );
      }

      if (browserTab && !browserTab.closed) {
        browserTab.location.replace(browserUrl);
      } else {
        // Fallback for environments that do not return a Window handle.
        window.open(browserUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      if (browserTab && !browserTab.closed) {
        browserTab.close();
      }
      setArtifactError(err?.message || `Unable to open ${config.label}.`);
    } finally {
      setArtifactLoading("");
    }
  };

  const job = payload?.job || payload || {};
  const status = normalizeStatus(job?.status || payload?.status || job?.progress?.stage);
  const ready = isComplete(status);
  const livePages = asPages(liveView);

  const contractType = contractTypeLabel(pickFirst(job?.schemaId, job?.contractType, payload?.schemaId, "contract"));
  const completedAt = pickFirst(job?.completedAt, job?.updatedAt, payload?.completedAt, payload?.updatedAt);


  const originalSourceUrl = pickFirst(
    source?.originalUrl,
    source?.original_url,
    source?.sourceFileUrl,
    source?.source_file_url,
    source?.sourceUrl,
    source?.source_url,
    source?.url,
    job?.sourceFileUrl,
    job?.source_file_url,
    job?.sourcePdfUrl,
    job?.source_pdf_url,
    job?.sourceUrl,
    job?.source_url,
    payload?.sourceFileUrl,
    payload?.source_file_url,
    payload?.sourcePdfUrl,
    payload?.source_pdf_url,
    payload?.sourceUrl,
    payload?.source_url
  );

  const analyzedPdfUrl = pickFirst(
    getAnalyzedPdfUrlFromPayload(source),
    getAnalyzedPdfUrlFromPayload(payload),
    getAnalyzedPdfUrlFromPayload(job),
    getAnalyzedPdfUrlFromPayload(liveViewPayload),
    getAnalyzedPdfUrlFromPayload(reportRoutePayload)
  );

  const activeDocumentUrl = documentView === "original" ? originalSourceUrl : analyzedPdfUrl;
  const activeDocumentLabel = documentView === "original" ? "Original upload" : "Analyzed document";

  const liveViewCompleteness =
    liveViewPayload?.liveViewCompleteness ||
    liveViewPayload?.live_view_completeness ||
    liveViewPayload?.liveView?.liveViewCompleteness ||
    liveViewPayload?.liveView?.live_view_completeness ||
    {};

  const liveMeta =
    liveViewPayload?.meta ||
    liveViewPayload?.metadata ||
    liveViewPayload?.liveView?.meta ||
    liveViewPayload?.live_view?.meta ||
    {};

  const liveRisk =
    liveMeta?.risk ||
    liveViewPayload?.risk ||
    liveViewPayload?.liveView?.risk ||
    liveViewPayload?.live_view?.risk ||
    {};

  const jobRisk = job?.risk || payload?.risk || {};

  const riskScore = pickFirstNumeric(
    liveRisk?.riskScore,
    liveRisk?.risk_score,
    jobRisk?.riskScore,
    jobRisk?.risk_score,
    job?.riskScore,
    job?.risk_score,
    getByPath(job, "report.risk.riskScore"),
    getByPath(job, "report.risk.risk_score")
  );

  const riskLevel = normalizeRiskLevel(
    pickFirst(
      liveRisk?.riskLevel,
      liveRisk?.risk_level,
      jobRisk?.riskLevel,
      jobRisk?.risk_level,
      job?.riskLevel,
      job?.risk_level,
      getByPath(job, "report.risk.riskLevel"),
      getByPath(job, "report.risk.risk_level")
    )
  );

  const riskTone = riskToneFromScore(riskScore, riskLevel);
  const riskDisplayLevel = riskTone?.label || riskLevel;

  const liveViewPageCount = pickFirstNumeric(
    liveViewCompleteness?.pageCount,
    liveViewCompleteness?.page_count,
    liveViewCompleteness?.liveViewPageCount,
    liveViewCompleteness?.live_view_page_count,
    getByPath(liveViewCompleteness, "upstream.liveViewPageCount"),
    getByPath(liveViewCompleteness, "upstream.live_view_page_count"),
    getByPath(job, "liveViewCompleteness.pageCount"),
    getByPath(job, "liveViewCompleteness.liveViewPageCount"),
    livePages.length
  );

  const liveViewHighlightCount = pickFirstNumeric(
    liveViewCompleteness?.paragraphCount,
    liveViewCompleteness?.paragraph_count,
    liveViewCompleteness?.liveViewParagraphCount,
    liveViewCompleteness?.live_view_paragraph_count,
    getByPath(liveViewCompleteness, "upstream.liveViewParagraphCount"),
    getByPath(liveViewCompleteness, "upstream.live_view_paragraph_count"),
    getByPath(job, "liveViewCompleteness.paragraphCount"),
    getByPath(job, "liveViewCompleteness.liveViewParagraphCount"),
    countParagraphsFromPages(livePages)
  );

  const pagesAnalyzed = pickFirstNumeric(
    job?.pagesAnalyzed,
    job?.pages_analyzed,
    getByPath(job, "ingest.pagesAnalyzed"),
    liveViewPageCount,
    livePages.length
  );

  const liveDashboardSummary = pickFirst(
    extractSummaryTextFromObject(liveViewPayload),
    extractSummaryTextFromObject(liveMeta),
    extractSummaryTextFromObject(liveView),
    extractSummaryTextFromObject(liveViewPayload?.liveView),
    extractSummaryTextFromObject(liveViewPayload?.live_view)
  );

  const reportDashboardSummary = extractSummaryTextFromObject(
    reportRoutePayload
  );

  const summary = pickFirst(
    liveDashboardSummary,
    reportDashboardSummary,
    job?.dashboardSummary,
    job?.dashboard_summary,
    job?.executiveSummary,
    job?.executive_summary,
    job?.overallFinding,
    job?.overall_finding,
    job?.summary,
    getByPath(job, "report.summary.overallFinding"),
    getByPath(job, "report.summary.overall_finding"),
    getByPath(job, "report.summary.executiveSummary"),
    getByPath(job, "report.summary.executive_summary"),
    getByPath(job, "summary.overallFinding"),
    getByPath(job, "summary.overall_finding"),
    getByPath(job, "summary.executiveSummary"),
    getByPath(job, "summary.executive_summary"),
    buildLiveViewSummary({
      riskScore,
      riskLevel: riskDisplayLevel,
      pageCount: liveViewPageCount || pagesAnalyzed,
      paragraphCount: liveViewHighlightCount,
      ready,
    })
  );

  const liveViewTone = liveViewColor();
  const hasLiveViewHighlights =
    liveViewHighlightCount !== "" &&
    liveViewHighlightCount !== undefined &&
    liveViewHighlightCount !== null;

  const coverageSnapshot = getCoverageSnapshotFromPayload(
    reportRoutePayload
  );

  const reportStats = [
    {
      title: "Found",
      value: coverageSnapshot.found,
      color: "#047857",
      background: "#ECFDF5",
      border: "#A7F3D0",
    },
    {
      title: "Unclear",
      value: coverageSnapshot.unclear,
      color: "#6D28D9",
      background: "#F5F3FF",
      border: "#DDD6FE",
    },
    {
      title: "Not Confirmed",
      value: coverageSnapshot.notConfirmed,
      color: "#B45309",
      background: "#FFFBEB",
      border: "#FDE68A",
    },
    {
      title: "Not Found",
      value: coverageSnapshot.notFound,
      color: "#B91C1C",
      background: "#FEF2F2",
      border: "#FECACA",
    },
    {
      title: "Live View Highlights",
      value: hasLiveViewHighlights ? liveViewHighlightCount : null,
      label:
        liveViewPageCount !== ""
          ? `Across ${liveViewPageCount} page${Number(liveViewPageCount) === 1 ? "" : "s"}`
          : "Clause review coverage",
      color: "#2563EB",
      background: "#EFF6FF",
      border: "#BFDBFE",
    },
  ];

  const viewerHeight = isCompact ? 460 : 560;
  const splitColumns = desktopViewMode === "both" ? "minmax(0, 1.08fr) minmax(0, 1fr)" : "minmax(0, 1fr)";

  const renderContractPanel = (withExpandControls = false) => (
    <section style={{ ...styles.splitPanel, minHeight: viewerHeight + 94 }}>
      <div className="contract-dashboard-panel-header" style={styles.panelHeaderRow}>
        <div style={styles.panelTitleWrap}>
          <h2 style={styles.panelTitle}>{activeDocumentLabel}</h2>
          <p style={styles.panelSubtitle}>
            {documentView === "analyzed"
              ? "Review the PDF created for Contract Insight analysis."
              : "Review the file originally uploaded by the user."}
          </p>
        </div>
        <div style={styles.panelActions}>
          <span className="contract-dashboard-small-pill" style={styles.smallPill}>{contractType}</span>
          {withExpandControls && (
            <button
              type="button"
              style={styles.panelToggleBtn}
              onClick={() =>
                setDesktopViewMode((mode) =>
                  mode === "contract" ? "both" : "contract"
                )
              }
            >
              {desktopViewMode === "contract" ? "Restore split" : "Expand"}
            </button>
          )}
        </div>
      </div>

      <div
        className="contract-dashboard-document-toggle"
        style={styles.documentToggleWrap}
        role="group"
        aria-label="Choose contract document"
      >
        <button
          type="button"
          className="contract-dashboard-document-toggle-button"
          aria-pressed={documentView === "analyzed"}
          data-selected={documentView === "analyzed" ? "true" : "false"}
          style={{
            ...styles.documentToggleButton,
            ...(documentView === "analyzed" ? styles.documentToggleButtonActive : {}),
          }}
          onClick={() => selectDocumentView("analyzed")}
        >
          Analyzed document
        </button>
        <button
          type="button"
          className="contract-dashboard-document-toggle-button"
          aria-pressed={documentView === "original"}
          data-selected={documentView === "original" ? "true" : "false"}
          style={{
            ...styles.documentToggleButton,
            ...(documentView === "original" ? styles.documentToggleButtonActive : {}),
          }}
          onClick={() => selectDocumentView("original")}
        >
          Original upload
        </button>
      </div>

      {documentView === "original" && sourceError && (
        <div style={styles.inlineError}>⚠️ {sourceError}</div>
      )}

      {activeDocumentUrl ? (
        <div style={styles.fileViewerWrap}>
          <ContractFileViewer
            key={`${documentView}:${activeDocumentUrl}`}
            url={activeDocumentUrl}
            height={viewerHeight}
          />
        </div>
      ) : (
        <div style={styles.emptyViewerBox}>
          {documentView === "analyzed"
            ? "Analyzed PDF preview is not available. The backend response must include a signed ingest PDF URL."
            : "Original upload preview is not available yet."}
        </div>
      )}
    </section>
  );

  const renderLivePanel = (withExpandControls = false) => (
    <section style={{ ...styles.splitPanelSoft, minHeight: viewerHeight + 94 }}>
      <div className="contract-dashboard-panel-header" style={styles.panelHeaderRow}>
        <div style={styles.panelTitleWrap}>
          <h2 style={styles.panelTitle}>Live View</h2>
          <p style={styles.panelSubtitle}>Clause highlights, explanations, and risk notes.</p>
        </div>
        <div style={styles.panelActions}>
          <span className="contract-dashboard-small-pill" style={styles.smallPill}>{liveViewPageCount || pagesAnalyzed || "—"} pages</span>
          {withExpandControls && (
            <button
              type="button"
              style={styles.panelToggleBtn}
              onClick={() =>
                setDesktopViewMode((mode) =>
                  mode === "live" ? "both" : "live"
                )
              }
            >
              {desktopViewMode === "live" ? "Restore split" : "Expand"}
            </button>
          )}
        </div>
      </div>

      {liveViewError && <div style={styles.inlineError}>⚠️ {liveViewError}</div>}

      {livePages.length > 0 ? (
        <div style={{ ...styles.liveViewScroll, maxHeight: viewerHeight }}>
          {livePages.map((page, idx) => {
            const pageNum = page.pageNumber || page.page || idx + 1;
            const pageKey = `page-${pageNum}`;
            const paras = Array.isArray(page.paragraphs) ? page.paragraphs : [];
            const isPageExpanded = expandedPageKey === pageKey;

            if (!paras.length) return null;

            return (
              <div key={pageKey} style={styles.pageBlock}>
                <div style={styles.pageHeaderRow}>
                  <div style={styles.pageHeader}>Page {pageNum}</div>
                  <button
                    type="button"
                    style={{
                      ...styles.explainButton,
                      ...(isPageExpanded ? styles.explainButtonActive : {}),
                    }}
                    onClick={() => setExpandedPageKey((cur) => (cur === pageKey ? null : pageKey))}
                  >
                    {isPageExpanded ? "Hide explanation" : "Explain this page"}
                  </button>
                </div>

                {isPageExpanded && (
                  <div style={styles.pageExplanationBox}>
                    {paras.map((para, pIdx) => {
                      const explanation = cleanDisplayText(
                        para?.explanation || para?.summary,
                        ""
                      );
                      const watch = Array.isArray(para?.watchOutFor)
                        ? para.watchOutFor.filter(Boolean)
                        : [];
                      if (!explanation && !watch.length) return null;
                      return (
                        <div key={`${pageKey}-exp-${pIdx}`} style={styles.pageExplanationItem}>
                          <div style={styles.pageExplanationHeading}>
                            {cleanDisplayText(para?.heading, `Section ${pIdx + 1}`)}
                          </div>
                          {explanation && <p style={styles.pageExplanationText}>{explanation}</p>}
                          {watch.length > 0 && (
                            <>
                              <div style={styles.tileLabel}>What to watch for</div>
                              <ul style={styles.watchList}>
                                {watch.map((item, wi) => (
                                  <li key={wi}>{cleanDisplayText(item, "")}</li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={styles.liveTileGrid}>
                  {paras.map((para, pIdx) => {
                    const tileKey = `${pageNum}-${para?.paraId || pIdx}`;
                    const expanded = expandedTileKey === tileKey;
                    const risk = para?.riskLevel || para?.risk_level || "";
                    const watch = Array.isArray(para?.watchOutFor)
                      ? para.watchOutFor.filter(Boolean)
                      : [];

                    return (
                      <button
                        key={tileKey}
                        type="button"
                        style={{
                          ...styles.liveTile,
                          ...paragraphRiskStyle(risk),
                          ...(expanded ? styles.liveTileExpanded : {}),
                        }}
                        onClick={() => setExpandedTileKey((cur) => (cur === tileKey ? null : tileKey))}
                      >
                        <div style={styles.liveTileTopRow}>
                          <div style={styles.liveTileHeading}>
                            {cleanDisplayText(para?.heading, "Untitled section")}
                          </div>
                        </div>
                        <p style={styles.liveTileSummary}>
                          {cleanDisplayText(para?.summary, "No summary available for this section.")}
                        </p>
                        {expanded && (
                          <div style={styles.tileExpandedBody}>
                            <div style={styles.tileLabel}>Plain-English explanation</div>
                            <p style={styles.tileExpandedText}>
                              {cleanDisplayText(
                                para?.explanation,
                                "No additional explanation was provided for this section."
                              )}
                            </p>
                            {watch.length > 0 && (
                              <>
                                <div style={styles.tileLabel}>What to watch for</div>
                                <ul style={styles.watchList}>
                                  {watch.map((item, wi) => (
                                    <li key={wi}>{cleanDisplayText(item, "")}</li>
                                  ))}
                                </ul>
                              </>
                            )}
                          </div>
                        )}

                        {risk && (
                          <div style={styles.riskPillRow}>
                            <span style={{ ...styles.riskPill, ...paragraphRiskPillStyle(risk) }}>
                              {cleanDisplayText(risk, "")}
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={styles.emptyViewerBox}>Live View is not available yet.</div>
      )}
    </section>
  );

  if (!authReady || loading) {
    return (
      <div className="contract-dashboard-page" style={styles.outerWrap}>
        <div style={styles.loadingCard}>
          <Spinner size={24} />
          <div style={styles.loadingTitle}>Loading Contract Insight dashboard…</div>
        </div>
      </div>
    );
  }

  if (!idToken) {
    return (
      <div className="contract-dashboard-page" style={styles.outerWrap}>
        <section style={styles.emptyState}>
          <div style={styles.emptyIcon}>◇</div>
          <h1 style={styles.emptyTitle}>Sign in to view this dashboard</h1>
          <p style={styles.emptyText}>
            Contract Insight dashboards are saved under your account after each report is completed.
          </p>
          <Link to="/login" style={styles.primaryButton}>Sign in</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="contract-dashboard-page" style={styles.outerWrap}>
      <style>{pageCss}</style>

      <header className="contract-dashboard-hero" style={styles.hero}>
        <div style={styles.heroTextWrap}>
          <div style={styles.eyebrow}>Contract Insight</div>
          <h1 className="contract-dashboard-title" style={styles.title}>Contract Insight Dashboard</h1>
          <p className="contract-dashboard-subtitle" style={styles.subtitle}>
            Review the original contract beside the Live View, clause summaries, and saved report artifacts.
          </p>
          <div className="contract-dashboard-completed-line" style={styles.completedLine}>
            {contractType} {completedAt ? `• Completed ${formatDateTime(completedAt)}` : ""}
          </div>
        </div>

        <div className="contract-dashboard-action-row" style={styles.actionRow}>
          <button
            type="button"
            onClick={() => openReportArtifact("summary")}
            disabled={!ready || Boolean(artifactLoading)}
            style={ready && !artifactLoading ? styles.primaryButton : styles.disabledButton}
            title="Open the Summary Report through AskLawly File Access"
          >
            {artifactLoading === "summary-pdf" ? "Opening…" : "Summary Report"}
          </button>

          <button
            type="button"
            onClick={() => openReportArtifact("pageReview")}
            disabled={!ready || Boolean(artifactLoading)}
            style={ready && !artifactLoading ? styles.secondaryButton : styles.disabledButton}
            title="Open the Page Review through AskLawly File Access"
          >
            {artifactLoading === "page-review-pdf" ? "Opening…" : "Page Review"}
          </button>

          <button
            type="button"
            onClick={() => openReportArtifact("termsGuide")}
            disabled={!ready || Boolean(artifactLoading)}
            style={ready && !artifactLoading ? styles.ghostButton : styles.disabledButton}
            title="Open the Terms Guide through AskLawly File Access"
          >
            {artifactLoading === "terms-guide-pdf" ? "Opening…" : "Terms Guide"}
          </button>
        </div>
      </header>

      <main style={styles.shell}>
        {error && <div style={styles.errorBox}>⚠️ {error}</div>}
        {artifactError && <div style={styles.noticeBox}>⚠️ {artifactError}</div>}

        {!ready && (
          <section style={styles.processingBox}>
            <div>
              <strong>This report is not ready yet.</strong>
              <p style={styles.processingText}>Open the status page to follow the current processing stage.</p>
            </div>
            <Link to={`/contract-insight/status?jobId=${encodeURIComponent(jobId)}`} style={styles.primaryButton}>
              Open Status Page
            </Link>
          </section>
        )}

        <section className="contract-dashboard-score-grid" style={styles.scoreGrid}>
          {reportStats.map((item) => (
            <ContractInsightScoreCard
              key={item.title}
              title={item.title}
              value={item.value}
              label={item.label}
              color={item.color}
              background={item.background}
              border={item.border}
            />
          ))}
        </section>

        <section style={styles.summaryPanel}>
          <div style={styles.panelHeaderCompact}>
            <h2 style={styles.summaryTitle}>Dashboard summary</h2>
          </div>
          <p style={styles.summaryText}>{cleanDisplayText(summary, "Summary is not available yet.")}</p>
        </section>

        {ready && !isCompact && (
          <section
            className="contract-dashboard-split-view"
            style={{ ...styles.splitViewSection, gridTemplateColumns: splitColumns }}
          >
            {desktopViewMode !== "live" && renderContractPanel(true)}
            {desktopViewMode !== "contract" && renderLivePanel(true)}
          </section>
        )}

        {ready && isCompact && (
          <section className="contract-dashboard-mobile-tabs" style={styles.mobileTabs}>
            <div style={styles.tabHeaderRow}>
              <button
                type="button"
                style={{ ...styles.tabButton, ...(activeMobileTab === "contract" ? styles.tabButtonActive : {}) }}
                onClick={() => setActiveMobileTab("contract")}
              >
                Contract
              </button>
              <button
                type="button"
                style={{ ...styles.tabButton, ...(activeMobileTab === "live" ? styles.tabButtonActive : {}) }}
                onClick={() => setActiveMobileTab("live")}
              >
                Live View
              </button>
            </div>
            <div style={styles.tabBody}>
              {activeMobileTab === "contract" ? renderContractPanel(false) : renderLivePanel(false)}
            </div>
          </section>
        )}

      </main>
    </div>
  );
}

function ContractInsightScoreCard({
  title,
  value,
  label = "",
  color = "#2563EB",
  background = "#FFFFFF",
  border = "rgba(148,163,184,0.22)",
}) {
  const hasValue = typeof value === "number" && Number.isFinite(value);
  const displayValue = hasValue ? Math.max(0, Math.trunc(value)) : "—";

  return (
    <article
      style={{
        ...styles.scoreCard,
        background,
        borderColor: border,
      }}
    >
      <div style={{ ...styles.scoreTitle, color }}>{title}</div>
      <div style={{ ...styles.coverageValue, color }}>{displayValue}</div>
      {label ? <div style={styles.scoreSubLabel}>{label}</div> : null}
    </article>
  );
}

function Spinner({ size = 22 }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "999px",
        border: "3px solid #DBEAFE",
        borderTopColor: "#1E2A78",
        display: "inline-block",
        animation: "contractDashboardSpin 0.8s linear infinite",
      }}
    />
  );
}

const pageCss = `
  @keyframes contractDashboardSpin { to { transform: rotate(360deg); } }

  html, body, #root {
    max-width: 100%;
    overflow-x: hidden;
  }

  .contract-dashboard-page,
  .contract-dashboard-page *,
  .contract-dashboard-hero,
  .contract-dashboard-score-grid,
  .contract-dashboard-split-view,
  .contract-dashboard-split-view > *,
  .contract-dashboard-mobile-tabs,
  .contract-dashboard-action-row {
    box-sizing: border-box;
    min-width: 0;
    max-width: 100%;
  }

  .contract-dashboard-page {
    width: 100%;
    overflow-x: hidden;
    contain: inline-size;
  }

  .contract-dashboard-subtitle {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 1023px) {
    .contract-dashboard-page {
      padding: 14px 12px 34px !important;
    }

    .contract-dashboard-hero {
      display: block !important;
      text-align: center !important;
    }

    .contract-dashboard-hero > div {
      margin-left: auto !important;
      margin-right: auto !important;
    }

    .contract-dashboard-title {
      font-size: 28px !important;
      line-height: 1.18 !important;
      text-align: center !important;
      overflow-wrap: anywhere !important;
    }

    .contract-dashboard-subtitle,
    .contract-dashboard-completed-line {
      margin-left: auto !important;
      margin-right: auto !important;
      text-align: center !important;
      overflow-wrap: anywhere !important;
      white-space: normal !important;
    }

    .contract-dashboard-action-row {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) !important;
      gap: 10px !important;
      width: 100% !important;
      margin-top: 18px !important;
    }

    .contract-dashboard-action-row a,
    .contract-dashboard-action-row button {
      width: 100% !important;
      max-width: 100% !important;
      white-space: normal !important;
      justify-content: center !important;
      text-align: center !important;
    }

    .contract-dashboard-score-grid {
      grid-template-columns: repeat(2, minmax(0, 360px)) !important;
      justify-content: center !important;
      gap: 12px !important;
    }

    .contract-dashboard-score-grid > :last-child {
      grid-column: 1 / -1 !important;
      justify-self: center !important;
      width: min(100%, 360px) !important;
    }

    .contract-dashboard-panel-header {
      display: flex !important;
      flex-wrap: nowrap !important;
      align-items: flex-start !important;
      justify-content: space-between !important;
      gap: 10px !important;
    }

    .contract-dashboard-small-pill {
      flex: 0 0 auto !important;
      white-space: nowrap !important;
      max-width: 46% !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }
  }

  @media (max-width: 620px) {
    .contract-dashboard-score-grid {
      grid-template-columns: minmax(0, 1fr) !important;
      width: 100% !important;
    }

    .contract-dashboard-score-grid > :last-child {
      grid-column: auto !important;
      justify-self: stretch !important;
      width: 100% !important;
    }
  }
`;

const styles = {
  outerWrap: {
    width: "100%",
    maxWidth: 1120,
    minWidth: 0,
    minHeight: "100vh",
    margin: "0 auto",
    overflowX: "hidden",
    background:
      "radial-gradient(ellipse 82% 68% at 58% 28%, rgba(37,99,235,0.08) 0%, rgba(37,99,235,0.04) 46%, rgba(37,99,235,0) 100%), radial-gradient(ellipse 72% 58% at 36% 72%, rgba(59,130,246,0.045) 0%, rgba(59,130,246,0.022) 48%, rgba(59,130,246,0) 100%), #FFFFFF",
    color: "#0F172A",
    fontFamily: "sans-serif",
    padding: "18px 16px 44px",
    boxSizing: "border-box",
  },
  shell: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    margin: "0 auto",
  },
  hero: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    margin: "0 auto 22px",
    paddingTop: 12,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 18,
  },
  heroTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  eyebrow: {
    color: "#2563EB",
    fontSize: 13,
    fontWeight: 950,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 7,
  },
  title: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.12,
    fontWeight: 950,
    color: "#0F172A",
    letterSpacing: "-0.035em",
  },
  subtitle: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.55,
    maxWidth: "100%",
  },
  completedLine: {
    marginTop: 10,
    color: "#475569",
    fontSize: 13,
    fontWeight: 800,
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    minWidth: 280,
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 42,
    padding: "0 16px",
    borderRadius: 12,
    border: "1px solid #1E2A78",
    background: "#1E2A78",
    color: "#FFFFFF",
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  secondaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 42,
    padding: "0 16px",
    borderRadius: 12,
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#1E2A78",
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  ghostButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 42,
    padding: "0 16px",
    borderRadius: 12,
    border: "1px solid #E2E8F0",
    background: "#F8FAFC",
    color: "#334155",
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 850,
    cursor: "pointer",
  },
  disabledButton: {
    minHeight: 42,
    padding: "0 16px",
    borderRadius: 12,
    border: "1px solid #CBD5E1",
    background: "#F1F5F9",
    color: "#64748B",
    fontSize: 13,
    fontWeight: 850,
    cursor: "not-allowed",
  },
  errorBox: {
    border: "1px solid #FECACA",
    background: "#FEF2F2",
    color: "#B91C1C",
    borderRadius: 14,
    padding: 12,
    marginBottom: 14,
    fontSize: 13,
    fontWeight: 700,
  },
  noticeBox: {
    border: "1px solid #FED7AA",
    background: "#FFF7ED",
    color: "#9A3412",
    borderRadius: 14,
    padding: 12,
    marginBottom: 14,
    fontSize: 13,
    fontWeight: 700,
  },
  processingBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    border: "1px solid #BFDBFE",
    background: "#EFF6FF",
    borderRadius: 18,
    padding: 16,
    marginBottom: 16,
  },
  processingText: { margin: "4px 0 0", color: "#475569", fontSize: 13, lineHeight: 1.5 },
  scoreGrid: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    margin: "0 auto 18px",
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    justifyContent: "center",
    gap: 12,
  },
  scoreCard: {
    minWidth: 0,
    minHeight: 92,
    overflow: "hidden",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: 16,
    padding: "13px 10px",
    background: "#FFFFFF",
    textAlign: "center",
    display: "grid",
    alignContent: "center",
    gap: 8,
    boxShadow: "0 10px 24px rgba(15,23,42,0.045)",
  },
  scoreTitle: {
    minHeight: 28,
    color: "#0F172A",
    fontSize: 11.5,
    fontWeight: 950,
    lineHeight: 1.25,
    display: "grid",
    placeItems: "center",
    overflowWrap: "anywhere",
  },
  coverageValue: {
    fontSize: 28,
    lineHeight: 1,
    fontWeight: 950,
    letterSpacing: "-0.035em",
  },
  gaugeWrap: {
    display: "grid",
    placeItems: "center",
  },
  gauge: {
    width: 112,
    height: 112,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
  },
  gaugeInner: {
    width: 78,
    height: 78,
    borderRadius: "50%",
    background: "#FFFFFF",
    display: "grid",
    placeItems: "center",
    alignContent: "center",
    boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.06)",
  },
  gaugeValue: {
    fontWeight: 950,
    fontSize: 25,
    lineHeight: 1,
  },
  gaugeUnit: {
    color: "#64748B",
    fontWeight: 800,
    fontSize: 11,
    lineHeight: 1,
    marginTop: 2,
  },
  scoreBarWrap: {
    minHeight: 112,
    display: "grid",
    alignContent: "center",
    gap: 10,
  },
  scoreBarTop: {
    display: "flex",
    justifyContent: "center",
    alignItems: "baseline",
    gap: 5,
    minWidth: 0,
  },
  scoreBig: {
    fontWeight: 950,
    fontSize: 36,
    lineHeight: 1,
    letterSpacing: "-0.04em",
  },
  scoreUnit: {
    color: "#64748B",
    fontWeight: 800,
    fontSize: 12,
    lineHeight: 1.2,
  },
  scoreTrack: {
    height: 12,
    borderRadius: 999,
    background: "#E2E8F0",
    overflow: "hidden",
  },
  scoreFill: {
    height: "100%",
    borderRadius: 999,
  },
  scoreSubLabel: {
    color: "#64748B",
    fontWeight: 750,
    fontSize: 11,
    lineHeight: 1.3,
    overflowWrap: "anywhere",
  },
  summaryPanel: {
    border: "1px solid #E2E8F0",
    borderRadius: 20,
    background: "rgba(255,255,255,0.94)",
    padding: 18,
    boxShadow: "0 14px 34px rgba(15,23,42,0.07)",
    marginBottom: 18,
  },
  panelHeaderCompact: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 10,
  },
  summaryTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 950,
    color: "#0F172A",
    letterSpacing: "-0.02em",
  },
  riskBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    border: "1px solid #E2E8F0",
    padding: "6px 10px",
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  summaryText: {
    margin: 0,
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.7,
  },
  splitViewSection: {
    marginTop: 16,
    display: "grid",
    gap: 16,
    alignItems: "stretch",
  },
  splitPanel: {
    border: "1px solid #E2E8F0",
    borderRadius: 20,
    padding: 12,
    background: "#FFFFFF",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 14px 34px rgba(15,23,42,0.07)",
  },
  splitPanelSoft: {
    border: "1px solid #E2E8F0",
    borderRadius: 20,
    padding: 12,
    background: "#F8FAFC",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 14px 34px rgba(15,23,42,0.06)",
  },
  panelHeaderRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 9,
  },
  panelTitleWrap: {
    minWidth: 0,
  },
  panelActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },
  documentToggleWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    alignItems: "stretch",
    alignSelf: "center",
    width: "100%",
    maxWidth: 430,
    gap: 4,
    padding: 4,
    marginBottom: 10,
    borderRadius: 12,
    border: "1px solid #CBD5E1",
    background: "#F1F5F9",
  },
  documentToggleButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minWidth: 0,
    minHeight: 42,
    padding: "8px 12px",
    borderRadius: 9,
    border: "1px solid transparent",
    background: "transparent",
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.2,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "normal",
    textAlign: "center",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    userSelect: "none",
    transition: "background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease, transform 80ms ease",
  },
  documentToggleButtonActive: {
    borderColor: "#1E2A78",
    background: "#1E2A78",
    color: "#FFFFFF",
    boxShadow: "0 3px 9px rgba(30,42,120,0.22)",
  },
  panelToggleBtn: {
    fontSize: 11,
    padding: "5px 9px",
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    cursor: "pointer",
    color: "#334155",
    fontWeight: 850,
    whiteSpace: "nowrap",
  },
  panelTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 950,
  },
  panelSubtitle: {
    margin: "3px 0 0",
    color: "#64748B",
    fontSize: 12,
    lineHeight: 1.4,
  },
  smallPill: {
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#334155",
    padding: "5px 9px",
    fontSize: 11,
    fontWeight: 900,
  },
  inlineError: {
    border: "1px solid #FECACA",
    background: "#FEF2F2",
    color: "#B91C1C",
    borderRadius: 12,
    padding: 10,
    marginBottom: 10,
    fontSize: 12,
  },
  fileViewerWrap: {
    flex: 1,
    width: "100%",
    border: "1px solid #CBD5E1",
    borderRadius: 14,
    overflow: "hidden",
    background: "#FFFFFF",
  },
  emptyViewerBox: {
    flex: 1,
    minHeight: 260,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px dashed #CBD5E1",
    borderRadius: 14,
    background: "#F8FAFC",
    color: "#64748B",
    textAlign: "center",
    padding: 18,
    fontSize: 13,
  },
  liveViewScroll: {
    flex: 1,
    overflowY: "auto",
    paddingRight: 4,
  },
  pageBlock: {
    marginBottom: 14,
  },
  pageHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  pageHeader: {
    color: "#334155",
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  explainButton: {
    border: "1px solid #C7D2FE",
    background: "#EEF2FF",
    color: "#3730A3",
    borderRadius: 999,
    padding: "5px 9px",
    fontSize: 11,
    fontWeight: 900,
    cursor: "pointer",
  },
  explainButtonActive: {
    background: "#3730A3",
    color: "#FFFFFF",
    borderColor: "#3730A3",
  },
  pageExplanationBox: {
    border: "1px solid #E2E8F0",
    borderRadius: 14,
    background: "#FFFFFF",
    padding: 10,
    marginBottom: 10,
  },
  pageExplanationItem: {
    borderTop: "1px dashed #E2E8F0",
    paddingTop: 8,
    paddingBottom: 8,
  },
  pageExplanationHeading: {
    fontSize: 13,
    fontWeight: 900,
    color: "#0F172A",
    marginBottom: 4,
  },
  pageExplanationText: {
    margin: 0,
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.55,
  },
  liveTileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
    gap: 9,
  },
  liveTile: {
    border: "1px solid #E2E8F0",
    borderRadius: 14,
    background: "#FFFFFF",
    padding: 10,
    textAlign: "left",
    cursor: "pointer",
    color: "#0F172A",
    boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
    display: "flex",
    flexDirection: "column",
  },
  liveTileExpanded: {
    boxShadow: "0 0 0 2px rgba(30,64,175,0.22)",
    background: "#EFF6FF",
  },
  liveTileHigh: { borderColor: "#FECACA", background: "#FEF2F2" },
  liveTileMedium: { borderColor: "#FED7AA", background: "#FFFBEB" },
  liveTileLow: { borderColor: "#BBF7D0", background: "#ECFDF5" },
  liveTileTopRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 6,
  },
  liveTileHeading: {
    fontSize: 13,
    fontWeight: 950,
    color: "#0F172A",
    lineHeight: 1.25,
  },
  liveTileSummary: {
    margin: 0,
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.5,
  },
  paraId: {
    marginTop: 8,
    color: "#64748B",
    fontSize: 11,
    fontWeight: 800,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  riskPillRow: {
    marginTop: "auto",
    paddingTop: 8,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  riskPill: {
    borderRadius: 999,
    padding: "3px 7px",
    fontSize: 10,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.45,
    whiteSpace: "nowrap",
  },
  riskPillHigh: { background: "#B91C1C", color: "#FFFFFF" },
  riskPillMedium: { background: "#C2410C", color: "#FFFFFF" },
  riskPillLow: { background: "#15803D", color: "#FFFFFF" },
  riskPillNeutral: { background: "#E2E8F0", color: "#334155" },
  tileExpandedBody: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: "1px dashed #CBD5E1",
  },
  tileLabel: {
    color: "#334155",
    fontSize: 11,
    fontWeight: 950,
    marginTop: 6,
    marginBottom: 4,
  },
  tileExpandedText: {
    margin: 0,
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.55,
  },
  watchList: {
    margin: "6px 0 0",
    paddingLeft: 18,
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.5,
  },
  mobileTabs: {
    marginTop: 16,
  },
  tabHeaderRow: {
    display: "flex",
    gap: 4,
    borderRadius: 999,
    background: "#E2E8F0",
    padding: 4,
    marginBottom: 10,
  },
  tabButton: {
    flex: 1,
    border: "none",
    borderRadius: 999,
    background: "transparent",
    color: "#334155",
    padding: "9px 10px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  tabButtonActive: {
    background: "#FFFFFF",
    color: "#1E2A78",
    boxShadow: "0 2px 6px rgba(15,23,42,0.16)",
  },
  tabBody: {
    width: "100%",
  },
  loadingCard: {
    width: "min(520px, 100%)",
    margin: "90px auto 0",
    borderRadius: 20,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    boxShadow: "0 20px 42px rgba(15, 23, 42, 0.1)",
    padding: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 12,
    color: "#0F172A",
    fontWeight: 850,
  },
  loadingTitle: {
    fontSize: 14,
    fontWeight: 900,
    color: "#334155",
  },
  emptyState: {
    width: "min(580px, 100%)",
    margin: "80px auto 0",
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: 24,
    padding: 34,
    textAlign: "center",
    boxShadow: "0 20px 42px rgba(15, 23, 42, 0.1)",
  },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#EEF2FF",
    color: "#1E2A78",
    fontSize: 26,
    fontWeight: 900,
    marginBottom: 14,
  },
  emptyTitle: { margin: 0, fontSize: 24, fontWeight: 950, color: "#0F172A" },
  emptyText: { color: "#64748B", fontSize: 14, lineHeight: 1.6, margin: "10px auto 18px", maxWidth: 430 },
};
